import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/apiConfig';
import { LoadingSpinner } from '../components/LoadingSpinner';

export const ExplainPage = () => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  const handleClear = () => {
    setQuestion('');
    setAnswer('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const apiUrl = getApiUrl('EXPLAIN', { question });
      const response = await fetch(apiUrl);
      const data = await response.text();
      setAnswer(data);
    } catch (error) {
      console.error('Error calling API:', error);
      setAnswer('Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 shadow-md">
        <div className="container mx-auto px-4">
          <button 
            className="mb-3 px-3 py-1 bg-white/20 rounded hover:bg-white/40 text-sm transition-all duration-300 active:scale-90 active:translate-x-[-4px]"
            onClick={() => navigate('/')}
          >
            ← Back
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <h1 className="text-3xl font-bold">Explain Like I'm 5</h1>
            </div>
            <p className="text-blue-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              An AI-powered tool to simplify complex text into easy-to-understand explanations
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="mb-6">
        <div className="mb-4">
          <label htmlFor="question" className="block mb-2 font-medium dark:text-white">
            Your Question:
          </label>
          <input
            id="question"
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Type your question here..."
            required
          />
        </div>
        
        <div className="flex space-x-3">
          <button 
            type="submit" 
            disabled={isLoading || !question || !!answer}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
          >
            {isLoading ? 'Loading...' : 'Get Explanation'}
          </button>
          
          <button 
            type="button" 
            onClick={handleClear}
            disabled={isLoading || (!question && !answer)}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
          >
            Clear
          </button>
        </div>
      </form>
      
      {isLoading ? (
        <div className="p-8 flex flex-col items-center justify-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600 dark:text-gray-300">Getting your explanation...</p>
        </div>
      ) : answer && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded border dark:border-gray-700">
          <h2 className="font-bold mb-2 dark:text-white">Response:</h2>
          <p className="whitespace-pre-wrap dark:text-gray-300">{answer}</p>
        </div>
      )}
      </div>
    </div>
  );
};
